import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { gamesRegistry } from '../data/gamesRegistry';
import gamesFeed from '../data/gamesFeed.json';

export const PortalLayout: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [liveResults, setLiveResults] = useState<any[]>([]);
  const [isSearchingLive, setIsSearchingLive] = useState(false);
  const navigate = useNavigate();

  // Prevent background scrolling when search overlay is open
  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSearchOpen]);

  const allGames = [...gamesRegistry, ...(gamesFeed as any[])];

  const slugify = (text: string) => {
    return text.toString().toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  // Live search effect
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setLiveResults([]);
      return;
    }
    
    setIsSearchingLive(true);
    const delayDebounceFn = setTimeout(() => {
      fetch(`https://gamemonetize.com/feed.php?format=0&num=10&page=1&name=${encodeURIComponent(searchQuery)}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const normalized = data.map(g => {
              const slug = `${slugify(g.title)}-${g.id}-gm`;
              return {
                id: slug,
                title: g.title,
                category: g.category,
                tags: g.tags ? g.tags.split(',').map((t: string) => t.trim()) : [],
                thumbnail: g.thumb,
                url: g.url,
                path: `/game/${slug}`,
                isFeedGame: true
              };
            });
            setLiveResults(normalized);
          }
        })
        .catch(console.error)
        .finally(() => setIsSearchingLive(false));
    }, 400); // 400ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const localResults = searchQuery.trim() === '' ? [] : allGames.filter(game => 
    game.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    game.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (game.tags && game.tags.some((t: string) => t.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  // Combine and deduplicate
  const combinedResults = [...localResults, ...liveResults].reduce((acc, curr) => {
    if (!acc.find((g: any) => g.id === curr.id)) acc.push(curr);
    return acc;
  }, []).slice(0, 10);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#f5f7f8', // Light background typical of game portals
      color: '#333'
    }}>
      <style>{`
        /* Global Custom Scrollbar / Sidebar Style */
        ::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 8px;
        }
        ::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 10px;
          border: 3px solid #f1f5f9;
        }
        ::-webkit-scrollbar-thumb:hover {
          background-color: #94a3b8;
        }
        body {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        @media (max-width: 600px) {
          .nav-links {
            display: none !important;
          }
          .logo-text {
            display: none !important;
          }
        }
      `}</style>
      
      {/* HEADER */}
      <header style={{
        backgroundColor: '#fff',
        padding: '10px 20px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 40
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/Logo192.webp" alt="Gamezolve" style={{ width: '40px', height: '40px', borderRadius: '10px' }} />
            <span className="logo-text" style={{ fontSize: '1.5rem', fontWeight: '900', color: '#111827', letterSpacing: '-0.5px' }}>gamezolve</span>
          </Link>
          
          <nav className="nav-links" style={{ display: 'flex', gap: '15px' }}>
            <Link to="/categories" style={{ textDecoration: 'none', color: '#4b5563', fontWeight: 'bold' }}>Categories</Link>
            <Link to="/blogs" style={{ textDecoration: 'none', color: '#4b5563', fontWeight: 'bold' }}>Blogs</Link>
          </nav>
        </div>
        
        <button 
          onClick={() => setIsSearchOpen(true)}
          style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#4b5563', transition: 'background-color 0.2s' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
        >
          <Search size={22} />
        </button>
      </header>

      {/* SEARCH OVERLAY */}
      {isSearchOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 20px',
          overflowY: 'auto'
        }}>
          <button 
            onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery('');
            }}
            style={{ position: 'absolute', top: '20px', right: '20px', background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#4b5563' }}
          >
            <X size={24} />
          </button>
          
          <div style={{ width: '100%', maxWidth: '600px', position: 'relative', marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '3px solid #3b82f6', paddingBottom: '10px', marginBottom: '30px' }}>
              <Search size={32} color="#3b82f6" style={{ marginRight: '15px' }} />
              <input 
                type="text" 
                placeholder="Search games..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                style={{ border: 'none', outline: 'none', fontSize: '1.8rem', width: '100%', background: 'transparent', color: '#111827', fontWeight: 'bold' }}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {combinedResults.length > 0 ? (
                combinedResults.map((game: any) => (
                  <Link 
                    key={game.id} 
                    to={game.path || `/game/${game.id}`}
                    state={{ game }}
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery('');
                    }}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '20px', 
                      padding: '15px', 
                      textDecoration: 'none', 
                      color: '#111827',
                      borderRadius: '15px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <img src={game.thumbnail} alt="" style={{ width: '80px', height: '80px', borderRadius: '15px', objectFit: 'cover', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '1.3rem', marginBottom: '5px' }}>{game.title}</div>
                      <div style={{ fontSize: '1rem', color: '#6b7280' }}>{game.category}</div>
                    </div>
                  </Link>
                ))
              ) : searchQuery.trim() !== '' ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '1.2rem' }}>
                  {isSearchingLive ? 'Searching for amazing games...' : `No games found for "${searchQuery}"`}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '1.1rem' }}>
                  Type to start searching through thousands of games.
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* MAIN CONTENT AREA */}
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>

      {/* FOOTER */}
      <footer style={{
        backgroundColor: '#111827',
        color: '#9ca3af',
        padding: '40px 20px',
        marginTop: 'auto'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/about" style={{ color: '#d1d5db', textDecoration: 'none' }}>About Us</Link>
            <Link to="/contact" style={{ color: '#d1d5db', textDecoration: 'none' }}>Contact</Link>
            <Link to="/faq" style={{ color: '#d1d5db', textDecoration: 'none' }}>FAQ</Link>
            <Link to="/terms" style={{ color: '#d1d5db', textDecoration: 'none' }}>Terms of Service</Link>
            <Link to="/privacy" style={{ color: '#d1d5db', textDecoration: 'none' }}>Privacy Policy</Link>
          </div>
          
          <div style={{ textAlign: 'center', maxWidth: '600px', fontSize: '0.9rem', lineHeight: '1.5' }}>
            <p>Gamezolve is your new home for premium web games. Play on your computer, tablet, or mobile phone for free.</p>
            <p style={{ marginTop: '10px' }}>&copy; {new Date().getFullYear()} Gamezolve. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
